import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import MediaQuery from 'react-responsive';
import { getRole } from '../../../../../../../reducers';
import { can } from '../../../../../../../services';
import { formatDollars } from '../../../../../../../utils/lineItemUtils';
import {
  requestPhaseItems,
  savePhaseItem,
  deletePhaseItem,
} from 'actionCreators/phase_items';
import { requestPricePatchLineItemList } from 'actionCreators/dropdown_lists';
import { getPhaseLineItemsWithTotal } from '../../../../../../../selectors/phaseLineItem';
import getPurchaseOrdersWithLabel from '../../../../../../../selectors/purchaseOrderOptions';
import LineItemTable from 'components/LineItemTable';
import LaborTableRow from './laborTableRow';
import MessageBox from 'components/MessageBox';
import phaseLineItemShape from 'types/lineItemPropType';
import purchaseOrderShape, { purchaseOrderOptionsType } from 'types/purchaseOrder';
import tableStyles from 'styles/table.scss';
import { CUT_POINT } from 'config/constants';
import swal from 'sweetalert';
import { error as parseError } from '../../../../../../../utils';

const LINE_ITEM_TYPE = 'phaseLineItems';
const EXCEPTION_MESSAGE = 'Please verify installer pay after adding exceptions!';

class PhaseLineItemTables extends Component {
  state = { tableMessages: {} }

  componentWillMount = () => this.refreshLineItemList();

  refreshLineItemList = (poId = null, removeTableMessage = false) => {
    this.props.requestPhaseItems(this.props.phaseId, LINE_ITEM_TYPE);
    // This is just fetching the POs and not doing anything with them,
    // just so that they're available for the isClosed check.
    this.props.requestPhaseItems(this.props.phaseId, 'purchaseOrders');
    if (removeTableMessage) {
      this.setState({
        tableMessages: { ...this.state.tableMessages, [poId]: '' },
      });
    }
  }

  handleDelete = lineItemId =>
    this.props.deletePhaseItem(this.props.phaseId, { id: lineItemId }, LINE_ITEM_TYPE);

  handleSave = lineItem => this.props.savePhaseItem(this.props.phaseId, lineItem, LINE_ITEM_TYPE)
    .then(() => (lineItem.id < 0 && lineItem.status === 'Exception' ?
      this.setState({ tableMessages: {
        ...this.state.tableMessages,
        [lineItem.purchase_order_id]: EXCEPTION_MESSAGE,
      } }) : null));

  requestLineItemOptions = () => this.props.requestPricePatchLineItemList(this.props.phaseId);

  isClosed = purchaseOrder => (purchaseOrder && (
    purchaseOrder.invoice_status === 'Invoiced' ||
    purchaseOrder.invoice_status === 'Recorded' ||
    purchaseOrder.invoice_status === 'Paid'
  ));

  handleMergeToOriginal = (poId) => {
    const { onMergeToOriginal } = this.props;
    if (!onMergeToOriginal) return;
    swal({
      title: 'Merge Exception PO?',
      text: 'All line items will be moved to the Original PO and this Exception will be marked as resolved.',
      icon: 'warning',
      buttons: ['Cancel', 'Merge'],
      dangerMode: true,
    }).then((shouldMerge) => {
      if (!shouldMerge) return;
      const result = onMergeToOriginal(poId);
      Promise.resolve(result)
        .catch((error) => {
          swal('Error', parseError(error), 'error');
        });
    });
  }

  renderLaborTable = ({ items, po, label, total }) => {
    const { tableMessages } = this.state;
    const { displayType, purchaseOrderOptions, role, cv, filterLineItems,
            onMergeToOriginal, canMergeToOriginal } = this.props;
    const editable = can(role, 'update', 'labor');
    const titleNote = (po && po.invoice_status && po.invoice_status === 'Not Billable')
      ? 'NOT BILLABLE' : '';
    const poTotal = po && po.total ? formatDollars(po.total) : 0;
    const liTotal = total ? formatDollars(total) : 0;
    const footer = (<div className={tableStyles.totalContainer}>
      {po && liTotal !== poTotal && <div className={tableStyles.totalWarning}>
        PO &amp; Line-Item totals don&apos;t match
      </div>}
      {po && <div className={tableStyles.total}>
        PO Total: {poTotal}
      </div>}
      <div className={tableStyles.total}>
        PO Line Item Total: {liTotal}
      </div>
    </div>);
    const tableMessageKey = po ? po.value : null;
    const shouldShowMergeButton = po && po.kind === 'Exception' && canMergeToOriginal && onMergeToOriginal;
    const mergeAction = shouldShowMergeButton ? [{
      icon: 'call_merge',
      text: 'Merge to Original',
      onClick: () => this.handleMergeToOriginal(po.value),
    }] : [];
    return (<div key={po ? po.value : -1}>
      {tableMessages[tableMessageKey] && <MessageBox
        error message={tableMessages[tableMessageKey]}
      />}
      <LineItemTable
        title={label}
        titleNote={titleNote}
        requestLineItemList={this.requestLineItemOptions}
        refreshList={() => this.refreshLineItemList(tableMessageKey, true)}
        readOnly={this.isClosed(po) || !editable}
        footer={footer}
        cv={cv}
        canViewTotal={false}
        accountingTable
        actions={mergeAction}
      >
        {items.map(item => <LaborTableRow
          key={item.id}
          onSave={this.handleSave}
          onDelete={this.handleDelete}
          lineItem={item}
          displayType={displayType}
          purchaseOrderOptions={purchaseOrderOptions.filter(
            purchaseOrderOption => purchaseOrderOption.number,
          )}
          purchaseOrderId={po ? po.value : null}
          readOnly={this.isClosed(po) || !editable}
          role={role}
          filterLineItems={filterLineItems}
        />)}
      </LineItemTable>
    </div>);
  }

  render = () => {
    const { poId, lineItemsByPo, cv } = this.props;
    if (cv) return (<div>{lineItemsByPo.map(this.renderLaborTable)}</div>);
    let singleLineItem;
    if (poId > 0) {
      singleLineItem = this.props.lineItemsByPo.filter(i => i.po && i.po.id === poId);
    }
    const lineItems = poId > 0 ? singleLineItem.map(this.renderLaborTable) :
      lineItemsByPo.map(this.renderLaborTable);
    return (<MediaQuery minWidth={CUT_POINT}>
      {(matches) => {
        if (matches) {
          return (<tr>
            <td className={tableStyles.tdIndention} />
            <td className={tableStyles.tdIndention} colSpan={'7'}>{lineItems}</td>
          </tr>);
        }
        return (<tr>
          <td colSpan={'8'}>{lineItems}</td>
        </tr>);
      }}
    </MediaQuery>);
  }
}

PhaseLineItemTables.defaultProps = {
  lineItemsByPo: [],
  poId: null,
  cv: false,
  filterLineItems: false,
  onMergeToOriginal: null,
  canMergeToOriginal: false,
};

PhaseLineItemTables.propTypes = {
  phaseId: PropTypes.oneOfType([
    PropTypes.string, PropTypes.number,
  ]),
  poId: PropTypes.number,
  lineItemsByPo: PropTypes.arrayOf(PropTypes.shape({
    po: PropTypes.shape(purchaseOrderShape),
    label: PropTypes.string.isRequired,
    items: PropTypes.arrayOf(PropTypes.shape(phaseLineItemShape)),
  })),
  requestPhaseItems: PropTypes.func,
  savePhaseItem: PropTypes.func,
  deletePhaseItem: PropTypes.func,
  requestPricePatchLineItemList: PropTypes.func,
  displayType: PropTypes.oneOf(['Contract', 'Accounting']),
  purchaseOrderOptions: purchaseOrderOptionsType,
  role: PropTypes.string.isRequired,
  cv: PropTypes.bool,
  filterLineItems: PropTypes.bool,
  onMergeToOriginal: PropTypes.func,
  canMergeToOriginal: PropTypes.bool,
};

const getItemsForPo = (lineItems, po = null) => {
  const items = lineItems.filter(item =>
    (item.purchase_order_id === (po ? po.value : null)) || item.id < 0,
  );
  const total = items.reduce((memo, lineItem) => memo + (lineItem.total || 0), 0);
  return { items, total };
};

const getPhaseLineItemsByPo = createSelector(
  getPhaseLineItemsWithTotal, getPurchaseOrdersWithLabel, (lineItems, purchaseOrders) => {
    const lineItemsByPo = purchaseOrders.filter(po => po.value > 0).map(po => (
      { ...getItemsForPo(lineItems, po), po, label: `PO: ${po.label}` }
    ));
    if (lineItems.some(item => item.purchase_order_id === null)) {
      lineItemsByPo.push({ ...getItemsForPo(lineItems), label: 'No PO' });
    }
    return lineItemsByPo;
  },
);

export default connect(state => ({
  lineItemsByPo: getPhaseLineItemsByPo(state),
  purchaseOrderOptions: getPurchaseOrdersWithLabel(state),
  role: getRole(state),
}), {
  requestPhaseItems,
  savePhaseItem,
  deletePhaseItem,
  requestPricePatchLineItemList,
})(PhaseLineItemTables);



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/phases/phaseShow/tabs/components/phaseLineItemTables.jsx